import { type MutableRefObject, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useModal } from '~/hooks/common';
import { useClaimStore } from '~/store';

import ClaimExchangeItemAddModal from '../ClaimExchangeItemAddModal';
import ClaimExchangeRegisterSecondStepExchangeDeliveryInfo from './ClaimExchangeRegisterSecondStepExchangeDeliveryInfo';
import ClaimExchangeRegisterSecondStepGoodsTable from './ClaimExchangeRegisterSecondStepGoodsTable';
import ClaimExchangeRegisterSecondStepReturnInfo from './ClaimExchangeRegisterSecondStepReturnInfo';

import type {
  TypeExchangeItemAddData,
  TypeExchangeRegisterSecondStepErrorFieldRefs,
  TypeExchangeRegisterSecondStepValues,
  TypeExchangeRegisterSecondStepValuesState,
} from '~/types/claim.types';

import styles from './ClaimExchangeRegisterSecondStepContent.module.scss';

const ClaimExchangeRegisterSecondStepContent = ({
  errorFieldRefs,
}: {
  errorFieldRefs: MutableRefObject<TypeExchangeRegisterSecondStepErrorFieldRefs>;
}) => {
  const [exchangeInfoValues, exchangeInfoValuesState, setExchangeInfoValues, setExchangeInfoValuesState] =
    useClaimStore(
      useShallow((state) => [
        state.exchangeRegisterSecondStepValues,
        state.exchangeRegisterSecondStepValuesState,
        state.actions.setExchangeRegisterSecondStepValues,
        state.actions.setExchangeRegisterSecondStepValuesState,
      ])
    );

  const [targetOrderItemId, setTargetOrderItemId] = useState<
    TypeExchangeRegisterSecondStepValues['exchangeOrderItems'][number]['itemId'] | null
  >(null);

  const exchangeItemAddModal = useModal({
    onOpen: (itemId?: TypeExchangeRegisterSecondStepValues['exchangeOrderItems'][number]['itemId']) => {
      if (!itemId) return;
      setTargetOrderItemId(itemId);
    },
    onConfirm: (selectedItems?: TypeExchangeItemAddData[]) => {
      if (!selectedItems) return;

      const newExchangeDeliveryItems = selectedItems.map((selectedItem) => ({
        itemName: selectedItem.skuName,
        itemId: selectedItem.skuCode,
        quantity: '',
      }));
      const isSameItemWithTarget =
        newExchangeDeliveryItems.length === 1 &&
        newExchangeDeliveryItems.some((newItem) => newItem.itemId === targetOrderItemId);

      setExchangeInfoValues({
        ...exchangeInfoValues,
        exchangeOrderItems: exchangeInfoValues.exchangeOrderItems.map((item) =>
          item.itemId === targetOrderItemId
            ? {
                ...item,
                isSameGoods: isSameItemWithTarget,
                exchangeDeliveryItems: [
                  ...item.exchangeDeliveryItems,
                  ...newExchangeDeliveryItems.filter((newItem) => {
                    const isAlreadyExists = item.exchangeDeliveryItems.some(
                      (existingItem) => existingItem.itemId === newItem.itemId
                    );
                    return !isAlreadyExists;
                  }),
                ],
              }
            : item
        ),
      });
    },
  });

  const handleChangeExchangeInfoValues = (values: Partial<TypeExchangeRegisterSecondStepValues>) => {
    setExchangeInfoValues({ ...exchangeInfoValues, ...values });
  };

  const handleChangeExchangeInfoValuesState = (values: Partial<TypeExchangeRegisterSecondStepValuesState>) => {
    setExchangeInfoValuesState({ ...exchangeInfoValuesState, ...values });
  };

  return (
    <div className={styles['content-container']}>
      <ClaimExchangeRegisterSecondStepReturnInfo
        data={exchangeInfoValues.returnInfo}
        dataState={exchangeInfoValuesState.returnInfo}
        onChange={handleChangeExchangeInfoValues}
        onChangeState={handleChangeExchangeInfoValuesState}
        errorFieldRefs={errorFieldRefs}
      />
      <ClaimExchangeRegisterSecondStepExchangeDeliveryInfo
        data={exchangeInfoValues.exchangeDeliveryInfo}
        dataState={exchangeInfoValuesState.exchangeDeliveryInfo}
        onChange={handleChangeExchangeInfoValues}
        onChangeState={handleChangeExchangeInfoValuesState}
        errorFieldRefs={errorFieldRefs}
      />
      <ClaimExchangeRegisterSecondStepGoodsTable
        data={exchangeInfoValues.exchangeOrderItems}
        dataState={exchangeInfoValuesState.exchangeOrderItems}
        onChange={handleChangeExchangeInfoValues}
        onChangeState={handleChangeExchangeInfoValuesState}
        onOpenExchangeItemAddModal={exchangeItemAddModal.handleOpenModal}
      />
      {exchangeItemAddModal.isOpen && (
        <ClaimExchangeItemAddModal
          isOpen={exchangeItemAddModal.isOpen}
          onCancel={exchangeItemAddModal.handleCloseModal}
          onConfirm={exchangeItemAddModal.handleConfirmModal}
        />
      )}
    </div>
  );
};

export default ClaimExchangeRegisterSecondStepContent;
